import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames/bind';
import Icon from '../Icon/Icon';

/**
 * `Badges` is a small container that displays state labels and icons.
 *
 * It supports rendering icons from an array of icon names, or you may pass icons - or any other component - as children
 * to this component.
 *
 * If no labels, icons or children are passed to this component, this component will not render anything.
 *
 * @property {node} children The icons and other child components you want to display to the right of the label.
 * @property {string} className The class name of this button. Will be appended to "core-badges".
 * @property {array} iconNames An array of icon names supported by the {@link Icon} component. If you want tooltips on
 * icons or want custom components you must pass the icons as children instead. Defaults to no icons.
 * @property {string} label The label want to display before the icons. This should be the state of your item such as
 * "Draft", "Modified", etc. May be used in combination with *labels*.
 * @property {array} labels Array of string labels. Use this prop when you need multiple state labels instead of just
 * one. May be used in combination with *label*.
 *
 * @see Icon
 */
export default class Badges extends PureComponent {

  static displayName = 'Badges';

  static propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
    iconNames: PropTypes.arrayOf(PropTypes.string),
    label: PropTypes.string,
    labels: PropTypes.arrayOf(PropTypes.string),
  };

  render() {
    const { children, className, iconNames, label, labels, ...rest } = this.props;
    let icons;

    if (iconNames) {
      icons = iconNames.map(name => <Icon key={name} name={name} className={name}/>);
    }
    if (label || labels || iconNames || children) {
      return (
        <span {...rest} className={cx('core-badges', className, { icons: icons || children })}>
          {label && <span className={cx('core-badges-tag', label)}>{label}</span>}
          {labels && labels.map(item => <span key={item} className={cx('core-badges-tag', item)}>{item}</span>)}
          {icons}
          {children}
        </span>
      );
    }
    return null;
  }

}
